/**
 * AirWaybillSVGPrint.js
 * Renders a pixel-perfect Air Waybill using the official SVG blank form as
 * a background img, overlaying all field values at exact mm coordinates.
 *
 * Every field has:
 *   x, y  – top-left position in mm
 *   w      – max width in mm  (hard-clipped)
 *   h      – max height in mm (hard-clipped, prevents bleed into adjacent cells)
 *   fs     – font-size in pt
 *   multi  – true = word-wrap allowed; false/absent = single line, truncate
 *   bold   – true = font-weight bold
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

const SVG_URL = '/awb_blank.svg';

// ─── FIELD MAP ──────────────────────────────────────────────────────────────
// All coordinates in mm on a 210×297 A4 canvas matching awb_blank.svg viewBox.
// h = maxHeight clips text so it never bleeds into the next row/cell.
// ────────────────────────────────────────────────────────────────────────────
const FIELDS = [
  // ── Top Margin Identifiers (Above the main bordered form) ──
  { key: 'top_margin_airline_code_1',  x: 15,  y: 17, w: 11.5, h: 5, fs: 10, bold: true },
  { key: 'top_margin_airport_code',    x: 24.5,  y: 17, w: 12, h: 5, fs: 10, bold: true },
  { key: 'top_margin_serial_number_1', x: 39,  y: 17, w: 30, h: 5, fs: 10, bold: true },
  { key: 'top_margin_mawb_label',      x: 140, y: 17, w: 60, h: 5, fs: 10, bold: true },

  // ── Shipper (box top y≈22, bottom y≈49 → available height ≈21mm from data start) ──
  { key: 'shipper_name_address',    x: 16.5, y: 27.5, w: 86,  h: 21,  fs: 7, multi: true, cutout: { w: 46, h: 10 } },
  { key: 'shipper_account_no',      x: 62,   y: 26,   w: 42,  h: 5,   fs: 10, bold: true },

  // ── Not Negotiable / AWB Number / Issued By (right column, top) ──
  { key: 'air_waybill_issued_by',   x: 140,  y: 29,   w: 65,  h: 10,  fs: 9,  bold: true, multi: true },

  // ── Consignee (box top y≈49, bottom y≈75 → available height ≈22mm from data start) ──
  { key: 'consignee_name_address',  x: 16.5, y: 52.5, w: 86,  h: 22,  fs: 7, multi: true, cutout: { w: 46, h: 10 } },
  { key: 'consignee_account_no',    x: 62,   y: 51,   w: 42,  h: 5,   fs: 10, bold: true },

  // ── Accounting Information (right column, lower) ──
  { key: 'accounting_information',  x: 107,  y: 78,   w: 100, h: 20,  fs: 7, multi: true },

  // ── Issuing Carrier's Agent (box top y≈75, bottom y≈91 → h≈13mm from data start) ──
  { key: 'issuing_agent_name_city', x: 16.5, y: 77.5, w: 88,  h: 11,  fs: 7, multi: true },

  // ── Agent IATA Code row ──
  { key: 'agent_iata_code',         x: 16.5, y: 93.5, w: 42,  h: 5,   fs: 10, bold: true },
  { key: 'account_no',              x: 62,   y: 93.5, w: 42,  h: 5,   fs: 10, bold: true },

  // ── Airport of Departure ──
  { key: 'airport_of_departure',    x: 16.5, y: 102,  w: 93,  h: 5,   fs: 10, bold: true },

  // ── Reference Number / Optional Shipping ──
  { key: 'reference_number',        x: 113,  y: 103,  w: 25,  h: 4,   fs: 6.5 },
  { key: 'optional_shipping_info',  x: 141,  y: 103,  w: 57,  h: 4,   fs: 6.5 },

  // ── Routing Row (column widths tightened to match SVG cell borders) ──
  { key: 'routing_to_1',            x: 16,   y: 110.5, w: 10,  h: 6,   fs: 7 },
  { key: 'routing_by_1',            x: 26,   y: 110.5, w: 44,  h: 6,   fs: 7 },
  { key: 'routing_to_2',            x: 70,   y: 110.5, w: 9,   h: 6,   fs: 7 },
  { key: 'routing_by_2',            x: 79,   y: 110.5, w: 9,   h: 6,   fs: 7 },
  { key: 'routing_to_3',            x: 88,   y: 110.5, w: 9,   h: 6,   fs: 7 },
  { key: 'routing_by_3',            x: 97,   y: 110.5, w: 9,   h: 6,   fs: 7 },

  // ── Currency / Charges row ──
  { key: 'currency',                x: 106,  y: 111.5,  w: 7,   h: 5,   fs: 7, textAlign: 'center' },
  { key: 'chgs_code',               x: 113.5, y: 111.5,  w: 7,   h: 5,   fs: 6, textAlign: 'center' },
  { key: 'ppd_wt',                  x: 120,  y: 111.5,  w: 5,   h: 5,   fs: 6, textAlign: 'center' },
  { key: 'coll_wt',                 x: 125,  y: 111.5,  w: 5,   h: 5,   fs: 6, textAlign: 'center' },
  { key: 'ppd_other',               x: 130,  y: 111.5,  w: 5,   h: 5,   fs: 6, textAlign: 'center' },
  { key: 'coll_other',              x: 135,  y: 111.5,  w: 5,   h: 5,   fs: 6, textAlign: 'center' },
  { key: 'declared_value_carriage', x: 140,  y: 111.5,  w: 30,  h: 5,   fs: 7, textAlign: 'center' },
  { key: 'declared_value_customs',  x: 170,  y: 111.5,  w: 30,  h: 5,   fs: 7, textAlign: 'center' },

  // ── Airport of Destination + Flight ──
  { key: 'airport_of_destination',  x: 18,   y: 118,   w: 52,  h: 6,   fs: 7, bold: true },
  { key: 'requested_flight',        x: 65,   y: 118,   w: 18,  h: 6,   fs: 7 },
  { key: 'requested_date',          x: 83,   y: 118,   w: 18,  h: 6,   fs: 7 },
  { key: 'amount_of_insurance',     x: 108,  y: 119.5, w: 24, h: 4,   fs: 6.5 },

  // ── Handling Information (box top y≈125, bottom y≈137 → h≈9mm from data start) ──
  { key: 'handling_information',    x: 16.5, y: 128,  w: 162, h: 9,   fs: 7, multi: true },
  { key: 'sci',                     x: 184,  y: 134,  w: 14,  h: 5,   fs: 6.5 },

  // ── Cargo Table — single row (y≈148 header row, data below) ──
  // Col boundaries: NOP 15-25 | GrossWt 25-43 | K/L 43-48 | RateCls 48-68 | Charg 68-88 | Rate 88-110 | Total 110-143 | Nature 143-200
  { key: 'no_of_pieces_rcp',        x: 16,   y: 149.5, w: 8,   h: 6,   fs: 7 },
  { key: 'gross_weight',            x: 26,   y: 149.5, w: 15,  h: 6,   fs: 7 },
  { key: 'kg_lb',                   x: 43,   y: 149.5, w: 4,   h: 6,   fs: 7 },
  { key: 'rate_class',              x: 49,   y: 149.5, w: 8,   h: 6,   fs: 7 },
  { key: 'commodity_item_no',       x: 49,   y: 154.5, w: 18,  h: 6,   fs: 7 },
  { key: 'chargeable_weight',       x: 69,   y: 149.5, w: 17,  h: 6,   fs: 7 },
  { key: 'rate_charge',             x: 89,   y: 149.5, w: 20,  h: 6,   fs: 7 },
  { key: 'total',                   x: 111,  y: 149.5, w: 30,  h: 6,   fs: 7 },
  { key: 'nature_quantity_goods',   x: 146,  y: 149.5, w: 52,  h: 49,  fs: 7, multi: true },

  // ── Charges Summary ──
  // Prepaid col x=15–50mm → data at x=17 | Collect col x=50–85mm → data at x=52
  { key: 'weight_charge_prepaid',              x: 18, y: 210.3, w:  20, h: 5, fs: 7 },
  { key: 'weight_charge_collect',              x: 53, y: 210.3, w:  20, h: 5, fs: 7 },
  { key: 'valuation_charge_prepaid',           x: 18, y: 219.3, w:  20, h: 5, fs: 7 },
  { key: 'valuation_charge_collect',           x: 53, y: 219.3, w:  20, h: 5, fs: 7 },
  { key: 'tax_prepaid',                        x: 18, y: 227.3, w:  20, h: 5, fs: 7 },
  { key: 'tax_collect',                        x: 53, y: 227.3, w:  20, h: 5, fs: 7 },
  { key: 'total_other_charges_agent_prepaid',  x: 18, y: 235.3, w:  20, h: 5, fs: 7 },
  { key: 'total_other_charges_agent_collect',  x: 53, y: 235.3, w:  20, h: 5, fs: 7 },
  { key: 'total_other_charges_carrier_prepaid',x: 18, y: 244.3, w:  20, h: 5, fs: 7 },
  { key: 'total_other_charges_carrier_collect',x: 53, y: 244.3, w:  20, h: 5, fs: 7 },
  { key: 'total_prepaid',                      x: 18, y: 261.3, w:  20, h: 5, fs: 7, bold: true },
  { key: 'total_collect',                      x: 53, y: 261.3, w:  20, h: 5, fs: 7, bold: true },

  // ── Other Charges (right half) ──
  { key: 'other_charges',           x: 87,   y: 212,  w: 110, h: 45, fs: 7, multi: true },

  // ── Currency Conversion / CC Charges ──
  { key: 'currency_conversion_rates',  x: 17,  y: 271, w: 32, h: 5, fs: 6.5 },
  { key: 'cc_charges_dest_currency',   x: 52,  y: 271, w: 32, h: 5, fs: 6.5 },
  { key: 'total_collect_charges',      x: 85,  y: 278.5, w: 35, h: 5, fs: 7, textAlign: 'center', bold: true },

  // ── Signatures / Execution ──
  { key: 'signature_shipper',          x: 142, y: 244, w: 55, h: 8,  fs: 7, textAlign: 'center' },
  { key: 'executed_on',                x: 85,  y: 266.8, w: 30, h: 5,  fs: 7, textAlign: 'center' },
  { key: 'at_place',                   x: 120, y: 266.8, w: 30, h: 5,  fs: 7, textAlign: 'center' },
  { key: 'signature_issuing_carrier',  x: 155, y: 266.8, w: 50, h: 5,  fs: 7, textAlign: 'center' },
];

// ─── HELPER: SPLIT NATURE & QUANTITY OF GOODS ────────────────────────────────
// The standard column (w: 52mm, h: 49mm) accommodates ~17 lines at 7pt.
// Overflow text beyond 17 visual lines shifts horizontally across columns 1-7
// (x: 16mm to 141mm, w: 125mm) below the cargo items and above the totals row.
function splitNatureOfGoods(text, maxLines = 17, maxCharsPerLine = 35) {
  if (!text) return { mainText: '', overflowText: '' };

  const rawLines = String(text).split('\n');
  const mainLines = [];
  const overflowLines = [];
  let currentVisualLines = 0;
  let inOverflow = false;

  for (let i = 0; i < rawLines.length; i++) {
    const rawLine = rawLines[i];

    if (inOverflow) {
      overflowLines.push(rawLine);
      continue;
    }

    const words = rawLine.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) {
      if (currentVisualLines + 1 <= maxLines) {
        mainLines.push(rawLine);
        currentVisualLines += 1;
      } else {
        inOverflow = true;
        overflowLines.push(rawLine);
      }
      continue;
    }

    const subLines = [];
    let cur = '';
    for (const w of words) {
      if (!cur) {
        if (w.length > maxCharsPerLine) {
          let rem = w;
          while (rem.length > maxCharsPerLine) {
            subLines.push(rem.substring(0, maxCharsPerLine));
            rem = rem.substring(maxCharsPerLine);
          }
          cur = rem;
        } else {
          cur = w;
        }
      } else if (cur.length + 1 + w.length <= maxCharsPerLine) {
        cur += ' ' + w;
      } else {
        subLines.push(cur);
        if (w.length > maxCharsPerLine) {
          let rem = w;
          while (rem.length > maxCharsPerLine) {
            subLines.push(rem.substring(0, maxCharsPerLine));
            rem = rem.substring(maxCharsPerLine);
          }
          cur = rem;
        } else {
          cur = w;
        }
      }
    }
    if (cur) subLines.push(cur);

    if (currentVisualLines + subLines.length <= maxLines) {
      mainLines.push(rawLine);
      currentVisualLines += subLines.length;
    } else {
      const available = maxLines - currentVisualLines;
      if (available > 0) {
        mainLines.push(subLines.slice(0, available).join('\n'));
        currentVisualLines += available;
        overflowLines.push(subLines.slice(available).join(' '));
      } else {
        overflowLines.push(rawLine);
      }
      inOverflow = true;
    }
  }

  // Flow overflow lines continuously across the full horizontal grid till Total column
  const formattedOverflow = overflowLines
    .map(line => line.trim())
    .filter(Boolean)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();

  return {
    mainText: mainLines.join('\n').trim(),
    overflowText: formattedOverflow,
  };
}

// ─── FIELD OVERLAY ───────────────────────────────────────────────────────────
// Renders one data field. Both width (w) and height (h) are enforced via
// explicit dimensions + overflow:hidden so text never bleeds into adjacent cells.
// ─────────────────────────────────────────────────────────────────────────────
function FieldOverlay({ field, value }) {
  if (value === null || value === undefined || value === '') return null;

  const base = {
    position: 'absolute',
    left:      `${field.x}mm`,
    top:       `${field.y}mm`,
    width:     `${field.w}mm`,
    maxWidth:  `${field.w}mm`,
    height:    field.h ? `${field.h}mm` : undefined,
    maxHeight: field.h ? `${field.h}mm` : undefined,
    fontSize:  `${field.fs || 6.5}pt`,
    fontWeight: field.bold ? '700' : '400',
    fontFamily: (field.key.startsWith('top_margin') || field.key.startsWith('nature_') || ['shipper_name_address', 'shipper_account_no', 'consignee_name_address', 'consignee_account_no', 'issuing_agent_name_city', 'agent_iata_code', 'account_no', 'airport_of_departure', 'air_waybill_issued_by', 'accounting_information', 'handling_information', 'no_of_pieces_rcp', 'no_of_pieces', 'gross_weight', 'kg_lb', 'rate_class', 'commodity_item_no', 'chargeable_weight', 'rate_charge', 'total', 'nature_quantity_goods', 'nature_quantity', 'nature_quantity_overflow', 'other_charges', 'sum_pieces', 'sum_weight', 'sum_total', 'weight_charge_prepaid', 'weight_charge_collect', 'valuation_charge_prepaid', 'valuation_charge_collect', 'tax_prepaid', 'tax_collect', 'total_other_charges_agent_prepaid', 'total_other_charges_agent_collect', 'total_other_charges_carrier_prepaid', 'total_other_charges_carrier_collect', 'total_prepaid', 'total_collect', 'total_collect_charges', 'executed_on', 'at_place', 'signature_issuing_carrier', 'signature_shipper', 'currency', 'chgs_code', 'ppd_wt', 'coll_wt', 'ppd_other', 'coll_other', 'declared_value_carriage', 'declared_value_customs', 'airport_of_destination', 'requested_flight', 'requested_date'].includes(field.key)) ? "'Open Sans', sans-serif" : "'Arial Narrow', Arial, sans-serif",
    color:     '#000',
    overflow:  'hidden',
    boxSizing: 'border-box',
    textTransform: 'uppercase',
    textAlign: ['top_margin_airline_code_1', 'top_margin_airport_code', 'shipper_account_no', 'consignee_account_no', 'routing_to_1', 'routing_by_1', 'routing_to_2', 'routing_by_2', 'routing_to_3', 'routing_by_3', 'requested_flight', 'requested_date', 'no_of_pieces_rcp', 'no_of_pieces', 'gross_weight', 'kg_lb', 'rate_class', 'commodity_item_no', 'chargeable_weight', 'rate_charge', 'total', 'sum_pieces', 'sum_weight', 'sum_total'].includes(field.key) ? 'center' : (field.key === 'top_margin_mawb_label' ? 'right' : (field.textAlign || 'left')),
  };

  if (field.multi) {
    return (
      <div style={{
        ...base,
        lineHeight:    '1.15',
        whiteSpace:    'pre-wrap',
        wordWrap:      'break-word',
      }}>
        {field.cutout && <div style={{ float: 'right', width: `${field.cutout.w}mm`, height: `${field.cutout.h}mm` }} />}
        {String(value)}
      </div>
    );
  }

  // Single-line: never wrap, clip
  return (
    <div style={{
      ...base,
      lineHeight:    '1.1',
      whiteSpace:    'nowrap',
      textOverflow:  'ellipsis',
    }}>
      {String(value)}
    </div>
  );
}

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
const AirWaybillSVGPrint = ({ hiddenMode = false, onReady = null, copyType = 'ALL', documentType = null, isDraft: isDraftProp = undefined }) => {
  const { docketId } = useParams();
  const [searchParams] = useSearchParams();
  const [formData, setFormData]   = useState(null);
  const [svgLoaded, setSvgLoaded] = useState(false);
  const [error, setError]         = useState(null);
  const didPrint                  = useRef(false);
  const docType    = documentType || searchParams.get('document_type') || 'AIR_WAYBILL';
  const isDraft    = isDraftProp !== undefined ? isDraftProp : (searchParams.get('draft') === 'true');
  const isHouse    = docType === 'HOUSE_AIR_WAYBILL' || searchParams.get('house') === 'true' || searchParams.get('is_house') === 'true' || Boolean(formData?.is_house);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  // Fetch saved AWB document data
  useEffect(() => {
    const token   = localStorage.getItem('auth_token');
    const headers = { Authorization: `Bearer ${token}` };
    const load = async () => {
      try {
        const [docRes, docketRes] = await Promise.all([
          fetch(`${backendUrl}/api/dockets/${docketId}/documents/${docType}`, { headers }),
          fetch(`${backendUrl}/api/dockets/${docketId}/print-data`, { headers })
        ]);
        
        if (!docRes.ok) throw new Error('Document not found — please save the Air Waybill form first.');
        const data = await docRes.json();
        setFormData(data.document_data);

        if (docketRes.ok) {
          const docketData = await docketRes.json();
          document.title = `${isHouse ? 'HOUSE AIRWAY BILL' : 'AIRWAY BILL'} - ${docketData?.docket?.system_docket_no || docketId}`;
        } else {
          document.title = `AIRWAY BILL - ${docketId}`;
        }
      } catch (e) {
        setError(e.message);
      }
    };
    if (docketId) load();
  }, [docketId, docType, backendUrl, isHouse]);

  // Auto-print only after BOTH data is loaded AND the SVG img has fully rendered.
  // Using requestAnimationFrame ensures React has painted all field overlays.
  useEffect(() => {
    if (formData && svgLoaded && !didPrint.current) {
      didPrint.current = true;
      if (!hiddenMode) {
        requestAnimationFrame(() => window.print());
      }
      if (onReady) {
        requestAnimationFrame(() => onReady());
      }
    }
  }, [formData, svgLoaded, hiddenMode, onReady]);

  const handleSvgLoad = useCallback(() => setSvgLoaded(true), []);

  // ── Error / Loading states ──
  if (error) return (
    <div style={{ padding: 32, fontFamily: 'sans-serif', color: '#dc2626' }}>
      <h2>Could not load Air Waybill</h2>
      <p>{error}</p>
      <button onClick={() => window.close()} style={{ marginTop: 12, padding: '8px 16px', cursor: 'pointer' }}>
        Close
      </button>
    </div>
  );

  if (!formData) return (
    <div style={{ padding: 32, fontFamily: 'sans-serif', color: '#374151' }}>
      ⏳ Loading Air Waybill…
    </div>
  );

  // Split nature of goods into standard column (~17 lines) and horizontal overflow box below cargo items
  const rawNatureText = formData?.nature_quantity_goods ||
    (Array.isArray(formData?.cargo_items) ? formData.cargo_items.map(it => it.nature_quantity).filter(Boolean).join('\n') : '') || '';
  const { mainText: natureMainText, overflowText: natureOverflowText } = splitNatureOfGoods(rawNatureText, 17, 35);

  const cargoRows = (Array.isArray(formData?.cargo_items) && formData.cargo_items.length > 0)
    ? formData.cargo_items.length
    : 1;
  const overflowY = Math.max(160, 149.5 + cargoRows * 8 + 2);
  const overflowH = Math.max(15, 198 - overflowY);

  let IATA_COPIES = isDraft ? [
    "DRAFT COPY"
  ] : [
    "ORIGINAL 1 (FOR ISSUING CARRIER)",
    "ORIGINAL 2 (FOR CONSIGNEE)",
    "ORIGINAL 3 (FOR SHIPPER)",
    "COPY 4 (DELIVERY RECEIPT)",
    "COPY 5 (FOR AIRPORT OF DESTINATION)",
    "COPY 6 (FOR THIRD CARRIER)",
    "COPY 7 (FOR SECOND CARRIER)",
    "COPY 8 (FOR FIRST CARRIER)",
    "COPY 9 (FOR AGENT)",
    "COPY 10 (EXTRA COPY)",
    "COPY 11 (EXTRA COPY)",
    "COPY 12 (EXTRA COPY)"
  ];

  if (hiddenMode && copyType !== 'ALL') {
    IATA_COPIES = [copyType];



  }

  return (
    <>
      {/* ── Screen-only toolbar + hint ── */}
      {!hiddenMode && (
      <div className="no-print" style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 10000,
        background: '#fef9c3', borderBottom: '2px solid #fde047',
        padding: '6px 16px', fontSize: 12, color: '#713f12',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 8,
      }}>
        <span>
          ⚠️ If form borders are missing in print, open <strong>More settings</strong> and enable <strong>"Background graphics"</strong>.
        </span>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <button
            onClick={() => window.print()}
            style={{ background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 18px', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}
          >
            🖨️ Print
          </button>
          <button
            onClick={() => window.close()}
            style={{ background: '#6b7280', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 13 }}
          >
            ✕ Close
          </button>
        </div>
      </div>
      )}

      {IATA_COPIES.map((copyName, index) => (
        <React.Fragment key={index}>
          {/* ── A4 Print Canvas ── */}
      <div
        className="awb-print-canvas"
        style={{
          position:      'relative',
          width:         '210mm',
          height:        '297mm',
          margin:        '0 auto',
          overflow:      'hidden',
          pageBreakAfter: 'always',
          background:    'white',
        }}
      >
        {/* SVG form template as <img> — always prints correctly.
            CSS background-image is stripped by browsers in print mode. */}
        <img
          src={SVG_URL}
          alt="Air Waybill Form"
          onLoad={handleSvgLoad}
          style={{
            position:      'absolute',
            top:           0,
            left:          0,
            width:         '210mm',
            height:        '297mm',
            display:       'block',
            pointerEvents: 'none',
            userSelect:    'none',
            zIndex:        0,
          }}
        />

        {/* ── Static field overlays (z-index 1, above SVG) ── */}
        <div style={{ position: 'absolute', inset: 0, zIndex: 1 }}>
          {FIELDS.map((f) => {
            const hasCargo = Array.isArray(formData.cargo_items) && formData.cargo_items.length > 0;
            const cargoFallbackKeys = ['no_of_pieces_rcp', 'gross_weight', 'kg_lb', 'rate_class', 'commodity_item_no', 'chargeable_weight', 'rate_charge', 'total'];
            if (hasCargo && cargoFallbackKeys.includes(f.key)) {
              return null;
            }
            let val = formData[f.key];
            let awbClean = String(formData.not_negotiable || formData.master_awb_no || '').replace(/\D/g, '');
            let pref = awbClean.substring(0,3);
            let ser = awbClean.substring(awbClean.length - 8);
            if (f.key === 'top_margin_airline_code_1') val = pref || '157';
            if (f.key === 'top_margin_airport_code') val = formData.airport_code || formData.airport_of_departure?.substring(0,3) || 'AMD';
            if (f.key === 'top_margin_serial_number_1') val = ser || '53258030';
            if (f.key === 'top_margin_mawb_label') {
              const awbNumber = `${pref || '157'} - ${ser || '53258030'}`;
              if (isHouse) {
                val = 'HAWB NO: ' + (formData.hawb_no || awbNumber);
              } else {
                val = awbNumber;
              }
            }
            if (f.key === 'nature_quantity_goods') val = natureMainText;
            if (f.key === 'total_prepaid' && !val) {
               const wt = parseFloat(formData.weight_charge_prepaid) || 0;
               const val_chg = parseFloat(formData.valuation_charge_prepaid) || 0;
               const tax = parseFloat(formData.tax_prepaid) || 0;
               const agent = parseFloat(formData.total_other_charges_agent_prepaid) || 0;
               const carrier = parseFloat(formData.total_other_charges_carrier_prepaid) || 0;
               const sum = wt + val_chg + tax + agent + carrier;
               if (sum > 0) val = sum.toFixed(2);
            }
            if (f.key === 'total_collect' && !val) {
               const wt = parseFloat(formData.weight_charge_collect) || 0;
               const val_chg = parseFloat(formData.valuation_charge_collect) || 0;
               const tax = parseFloat(formData.tax_collect) || 0;
               const agent = parseFloat(formData.total_other_charges_agent_collect) || 0;
               const carrier = parseFloat(formData.total_other_charges_carrier_collect) || 0;
               const sum = wt + val_chg + tax + agent + carrier;
               if (sum > 0) val = sum.toFixed(2);
            }
            return <FieldOverlay key={f.key} field={f} value={val} />;
          })}

          {/* ── Multi-cargo rows — iterates cargo_items[] if present ──
              Each row is 8mm tall; first data row starts at y=162mm.
              Falls back to single-row FIELDS above if cargo_items is absent. */}
          {Array.isArray(formData.cargo_items) && formData.cargo_items.map((item, idx) => {
            const rowY = 149.5 + idx * 8;
            const CARGO_COLS = [
              { key: 'no_of_pieces',      x: 16,  w: 8,  h: 7, fs: 7 },
              { key: 'gross_weight',      x: 26,  w: 15, h: 7, fs: 7 },
              { key: 'kg_lb',            x: 43,  w: 4,  h: 7, fs: 7 },
              { key: 'rate_class',       x: 49,  w: 18, h: 7, fs: 7 },
              { key: 'chargeable_weight',x: 69,  w: 17, h: 7, fs: 7 },
              { key: 'rate_charge',      x: 89,  w: 20, h: 7, fs: 7 },
              { key: 'total',            x: 111, w: 30, h: 7, fs: 7 },

            ];
            return CARGO_COLS.map((col) => (
              <FieldOverlay
                key={`cargo-${idx}-${col.key}`}
                field={{ ...col, y: rowY }}
                value={item[col.key]}
              />
            ));
          })}
          
          {/* ── Overflow Nature & Quantity of Goods (Stuck to bottom of cargo grid, expands upwards) ── */}
          {natureOverflowText && (
            <div
              style={{
                position: 'absolute',
                left: '16mm',
                top: `${overflowY}mm`,
                width: '125mm',
                maxWidth: '125mm',
                height: `${overflowH}mm`,
                maxHeight: `${overflowH}mm`,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                overflow: 'hidden',
                boxSizing: 'border-box',
                pointerEvents: 'none',
              }}
            >
              <div
                style={{
                  marginTop: 'auto',
                  fontSize: '7pt',
                  fontFamily: "'Open Sans', sans-serif",
                  fontWeight: '400',
                  color: '#000',
                  lineHeight: '1.15',
                  textTransform: 'uppercase',
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                  paddingBottom: '0.8mm',
                }}
              >
                {natureOverflowText}
              </div>
            </div>
          )}

          {/* Dynamic Summary Row for Cargo Totals */}
          {(() => {
            const hasItems = Array.isArray(formData.cargo_items) && formData.cargo_items.length > 0;
            const sumPieces = hasItems
              ? formData.cargo_items.reduce((acc, it) => acc + (parseFloat(it.no_of_pieces) || 0), 0)
              : (parseFloat(formData.no_of_pieces_rcp) || 0);
            
            const sumWeight = hasItems
              ? formData.cargo_items.reduce((acc, it) => acc + (parseFloat(it.gross_weight) || 0), 0)
              : (parseFloat(formData.gross_weight) || 0);
              
            const sumTotal = hasItems
              ? formData.cargo_items.reduce((acc, it) => acc + (parseFloat(it.total) || 0), 0)
              : (parseFloat(formData.total) || 0);

            const summaryY = 199.5;
            return (
              <React.Fragment>
                {sumPieces > 0 && <FieldOverlay field={{ key: 'sum_pieces', x: 16, y: summaryY, w: 8, h: 6, fs: 7, bold: true }} value={sumPieces} />}
                {sumWeight > 0 && <FieldOverlay field={{ key: 'sum_weight', x: 26, y: summaryY, w: 15, h: 6, fs: 7, bold: true }} value={sumWeight.toFixed(3)} />}
                {sumTotal > 0 && <FieldOverlay field={{ key: 'sum_total', x: 111, y: summaryY, w: 30, h: 6, fs: 7, bold: true }} value={sumTotal.toFixed(2)} />}
              </React.Fragment>
            );
          })()}
        </div>

        {/* ── Copy Name Overlay ── */}
        <div style={{
          position: 'absolute',
          bottom: '8mm',
          right: '15mm',
          fontSize: '11pt',
          fontWeight: 'bold',
          fontFamily: "'Open Sans', sans-serif",
          textAlign: 'right',
          zIndex: 10
        }}>
          {copyName}
        </div>

        {isDraft && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%) rotate(-45deg)',
            fontSize: '120pt',
            fontWeight: 'bold',
            color: 'rgba(150, 150, 150, 0.2)',
            zIndex: 5,
            pointerEvents: 'none',
            fontFamily: 'sans-serif'
          }}>
            DRAFT
          </div>
        )}
      </div>

      {/* ── PAGE 2: IATA Terms and Conditions ── */}
      <div className="awb-terms-page" style={{
        position: 'relative',
        width: '210mm',
        height: '297mm',
        margin: '0 auto',
        padding: '10mm 15mm',
        backgroundColor: '#fff',
        fontFamily: "'Open Sans', sans-serif",
        fontSize: '6pt',
        fontWeight: 'normal',
        lineHeight: '1.15',
        boxSizing: 'border-box',
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
        textRendering: 'optimizeLegibility',
        pageBreakAfter: 'always'
      }}>
        {isDraft && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%) rotate(-45deg)',
            fontSize: '120pt',
            fontWeight: 'bold',
            color: 'rgba(150, 150, 150, 0.2)',
            zIndex: 0,
            pointerEvents: 'none',
            fontFamily: 'sans-serif'
          }}>
            DRAFT
          </div>
        )}
        <div style={{ textAlign: 'center', marginBottom: '6mm', position: 'relative', zIndex: 1 }}>
          <svg xmlns="http://www.w3.org/2000/svg" style={{ display: 'block', margin: '0 auto', height: '16mm' }} viewBox="0 0 35.496 21.663">
            <path d="M17.579 7.963a5.22 5.22 0 0 0-1.78.373c.259.509.581.93.97 1.32.253.253.523.468.809.644l.001-2.337zM6.611 14.728l-1.709 6.935h2.153l1.674-6.935zM15.869 21.663l-1.435-6.935h-2.152l-3.587 6.935h2.083l.444-.854h2.433l.129.854h2.085zm-2.426-2.221h-1.605l1.23-2.528.383 2.536-.008-.008zM23.521 14.728h-6.218l-.307 1.776h2.05l-.888 5.159h2.118l.888-5.159h2.016zM29.944 21.663l-1.435-6.935h-2.152l-3.587 6.935h2.084l.444-.854h2.434l.129.854h2.083zm-2.426-2.221h-1.605l1.23-2.528.384 2.54-.009-.012zM24.496 7.105c-.651 2.187-2.479 3.913-4.803 4.542.087.553 1.027 1.783 2.411 1.783h6.406c.29 0 .598-.188.837-.41.239-.222.581-.632.769-.923h-5.91c-.273 0-.427-.478.119-.478h6.286c.359-.017 1.179-.786 1.333-1.127h-6.747a.24.24 0 0 1 0-.479h7.191c.41-.102.974-.546 1.265-1.127h-7.448c-.325 0-.325-.479 0-.479h7.789c.581-.103 1.315-.837 1.503-1.298l-11.001-.004zM11.291 12.097H5.38c.188.291.53.701.769.923.239.222.546.41.837.41h6.406c1.383 0 2.323-1.23 2.41-1.783-2.323-.629-4.151-2.355-4.802-4.542L0 7.11c.188.461.922 1.195 1.503 1.298h7.789c.324 0 .324.479 0 .479H1.845c.291.581.854 1.025 1.264 1.127H10.3a.24.24 0 0 1 0 .479H3.553c.153.342.974 1.11 1.332 1.127h6.286c.547-.001.393.477.12.477zM15.66 8.01a5.575 5.575 0 0 1 1.917-.39V5.47h-2.47c.017.89.222 1.78.553 2.54zM13.944 9.171a4.851 4.851 0 0 1 1.407-1.025 7.059 7.059 0 0 1-.586-2.673h-2.494c.037 1.472.624 2.764 1.673 3.698zM17.429 10.591a4.408 4.408 0 0 1-.902-.694 5.488 5.488 0 0 1-1.026-1.429c-.481.231-.92.538-1.292.911.87.688 2.019 1.133 3.22 1.212zM17.577 2.97a5.552 5.552 0 0 1-1.917-.39 6.8 6.8 0 0 0-.553 2.541h2.47V2.97zM15.799 2.255a5.22 5.22 0 0 0 1.78.373L17.578.292a4.416 4.416 0 0 0-.809.644c-.39.389-.711.81-.97 1.319zM15.351 2.444a4.851 4.851 0 0 1-1.407-1.025c-1.049.934-1.636 2.227-1.672 3.699h2.494c.016-.947.234-1.87.585-2.674zM16.527.694c.287-.287.572-.498.902-.694-1.202.079-2.351.524-3.221 1.212.373.373.811.679 1.292.911A5.514 5.514 0 0 1 16.527.694zM17.918 7.621a5.552 5.552 0 0 1 1.917.39 6.8 6.8 0 0 0 .553-2.541h-2.471v2.151zM18.727 9.656c.389-.39.71-.811.97-1.32a5.22 5.22 0 0 0-1.78-.373v2.336c.286-.175.556-.39.81-.643zM23.224 5.473H20.73a7.083 7.083 0 0 1-.586 2.674 4.856 4.856 0 0 1 1.408 1.025c1.048-.935 1.636-2.227 1.672-3.699zM18.066 10.591c1.202-.079 2.35-.524 3.221-1.212a4.717 4.717 0 0 0-1.293-.911 5.488 5.488 0 0 1-1.026 1.429 4.38 4.38 0 0 1-.902.694zM19.836 2.581a5.575 5.575 0 0 1-1.917.39v2.151h2.471a6.816 6.816 0 0 0-.554-2.541zM18.727.936a4.416 4.416 0 0 0-.809-.644v2.336a5.22 5.22 0 0 0 1.78-.373 5.165 5.165 0 0 0-.971-1.319zM20.73 5.118h2.494c-.036-1.472-.624-2.765-1.672-3.699a4.842 4.842 0 0 1-1.408 1.025c.351.804.569 1.727.586 2.674zM19.995 2.123c.482-.232.92-.539 1.293-.911C20.417.524 19.268.079 18.066 0c.33.196.615.407.902.694.416.416.751.885 1.027 1.429z" />
          </svg>
        </div>
        <h3 style={{ textAlign: 'center', fontSize: '9pt', fontWeight: 'bold', marginBottom: '3mm' }}>NOTICE CONCERNING CARRIER'S LIMITATION OF LIABILITY</h3>
        <p style={{ textAlign: 'justify', marginBottom: '3mm' }}>If the carriage involves an ultimate destination or stop in a country other than the country of departure, the Montreal Convention or the Warsaw Convention may be applicable to the liability of the Carrier in respect of loss of, damage or delay to cargo. Carrier's limitation of liability in accordance with those Conventions shall be as set forth in subparagraph 4 unless a higher value is declared.</p>
        
        <h3 style={{ textAlign: 'center', fontSize: '9pt', fontWeight: 'bold', marginBottom: '3mm' }}>CONDITIONS OF CONTRACT</h3>
        
        <div style={{ columnCount: 2, columnGap: '8mm', textAlign: 'justify' }}>
          <ol style={{ paddingLeft: '4mm', margin: 0, listStyleType: 'decimal' }}>
            <li style={{ marginBottom: '1mm' }}>
              <div style={{ marginBottom: '1mm' }}>In this contract and the Notices appearing hereon:</div>
              <div style={{ marginBottom: '1mm' }}>CARRIER includes the air carrier issuing this air waybill and all carriers that carry or undertake to carry the cargo or perform any other services related to such carriage.</div>
              <div style={{ marginBottom: '1mm' }}>SPECIAL DRAWING RIGHT (SDR) is a Special Drawing Right as defined by the International Monetary Fund.</div>
              <div style={{ marginBottom: '1mm' }}>WARSAW CONVENTION means whichever of the following instruments is applicable to the contract of carriage:
                <ul style={{ paddingLeft: '4mm', marginTop: '1mm', marginBottom: '1mm' }}>
                  <li>the Convention for the Unification of Certain Rules Relating to International Carriage by Air, signed at Warsaw, 12 October 1929;</li>
                  <li>that Convention as amended at The Hague on 28 September 1955;</li>
                  <li>that Convention as amended at The Hague 1955 and by Montreal Protocol No. 1, 2, or 4 (1975) as the case may be.</li>
                </ul>
              </div>
              <div>MONTREAL CONVENTION means the Convention for the Unification of Certain Rules for International Carriage by Air, done at Montreal on 28 May 1999.</div>
            </li>
            
            <li style={{ marginBottom: '1mm', listStyleType: 'none' }}>
              <div style={{ fontWeight: 'bold' }}>2.</div>
              <div style={{ marginLeft: '4mm' }}>
                <div style={{ marginBottom: '1mm' }}><strong>2.1</strong> Carriage is subject to the rules relating to liability established by the Warsaw Convention or the Montreal Convention unless such carriage is not "international carriage" as defined by the applicable Conventions.</div>
                <div style={{ marginBottom: '1mm' }}><strong>2.2</strong> To the extent not in conflict with the foregoing, carriage and other related services performed by each Carrier are subject to:</div>
                <div style={{ marginLeft: '4mm', marginBottom: '1mm' }}><strong>2.2.1</strong> applicable laws and government regulations;</div>
                <div style={{ marginLeft: '4mm', marginBottom: '1mm' }}><strong>2.2.2</strong> provisions contained in the air waybill, Carrier's conditions of carriage and related rules, regulations, and timetables (but not the times of departure and arrival stated therein) and applicable tariffs of such Carrier, which are made part hereof, and which may be inspected at any airports or other cargo sales offices from which it operates regular services.</div>
                <div style={{ marginLeft: '4mm', marginBottom: '1mm' }}>When carriage is to/from the USA, the shipper and the consignee are entitled, upon request, to receive a free copy of the Carrier's conditions of carriage. The Carrier's conditions of carriage include, but are not limited to:</div>
                <div style={{ marginLeft: '8mm', marginBottom: '1mm' }}><strong>2.2.2.1</strong> limits on the Carrier's liability for loss, damage or delay of goods, including fragile or perishable goods;</div>
                <div style={{ marginLeft: '8mm', marginBottom: '1mm' }}><strong>2.2.2.2</strong> claims restrictions, including time periods within which shippers or consignees must file a claim or bring an action against the Carrier for its acts or omissions, or those of its agents;</div>
                <div style={{ marginLeft: '8mm', marginBottom: '1mm' }}><strong>2.2.2.3</strong> rights, if any, of the Carrier to change the terms of the contract;</div>
                <div style={{ marginLeft: '8mm', marginBottom: '1mm' }}><strong>2.2.2.4</strong> rules about Carrier's right to refuse to carry;</div>
                <div style={{ marginLeft: '8mm', marginBottom: '1mm' }}><strong>2.2.2.5</strong> rights of the Carrier and limitations concerning delay or failure to perform service, including schedule changes, substitution of alternate Carrier or aircraft and rerouting.</div>
              </div>
            </li>

            <li style={{ marginBottom: '1mm' }} value="3">
              <div style={{ marginBottom: '1mm' }}>The agreed stopping places (which may be altered by Carrier in case of necessity) are those places, except the place of departure and place of destination, set forth on the face hereof or shown in Carrier's timetables as scheduled stopping places for the route.</div>
              <div>Carriage to be performed hereunder by several successive Carriers is regarded as a single operation.</div>
            </li>

            <li style={{ marginBottom: '1mm' }}>
              <div>For carriage to which the Montreal Convention does not apply, Carrier's liability limitation for cargo lost, damaged or delayed shall be 26 SDRs per kilogram unless a greater per kilogram monetary limit is provided in any applicable Convention or in Carrier's tariffs or general conditions of carriage.</div>
            </li>

            <li style={{ marginBottom: '1mm', listStyleType: 'none' }}>
              <div style={{ fontWeight: 'bold' }}>5.</div>
              <div style={{ marginLeft: '4mm' }}>
                <div style={{ marginBottom: '1mm' }}><strong>5.1</strong> Except when the Carrier has extended credit to the consignee without the written consent of the shipper, the shipper guarantees payment of all charges for the carriage due in accordance with Carrier's tariff, conditions of carriage and related regulations, applicable laws (including national laws implementing the Warsaw Convention and the Montreal Convention), government regulations, orders and requirements.</div>
                <div><strong>5.2</strong> When no part of the consignment is delivered, a claim with respect to such consignment will be considered even though transportation charges thereon are unpaid.</div>
              </div>
            </li>

            <li style={{ marginBottom: '1mm', listStyleType: 'none' }}>
              <div style={{ fontWeight: 'bold' }}>6.</div>
              <div style={{ marginLeft: '4mm' }}>
                <div style={{ marginBottom: '1mm' }}><strong>6.1</strong> For cargo accepted for carriage, the Warsaw Convention and the Montreal Convention permit shipper to increase the limitation of liability by declaring a higher value for carriage and paying a supplemental charge if required.</div>
                <div><strong>6.2</strong> In carriage to which neither the Warsaw Convention nor the Montreal Convention applies, Carrier shall, in accordance with the procedures set forth in its general conditions of carriage and applicable tariffs, permit shipper to increase the limitation of liability by declaring a higher value for carriage and paying a supplemental charge if so required.</div>
              </div>
            </li>

            <li style={{ marginBottom: '1mm', listStyleType: 'none' }}>
              <div style={{ fontWeight: 'bold' }}>7.</div>
              <div style={{ marginLeft: '4mm' }}>
                <div style={{ marginBottom: '1mm' }}><strong>7.1</strong> In cases of loss of, damage or delay to part of the cargo, the weight to be taken into account in determining Carrier's limit of liability shall be only the weight of the package or packages concerned.</div>
                <div style={{ marginBottom: '1mm' }}><strong>7.2</strong> Notwithstanding any other provisions, for "foreign air transportation" as defined by the U.S. Transportation Code:</div>
                <div style={{ marginLeft: '4mm', marginBottom: '1mm' }}><strong>7.2.1</strong> in the case of loss of, damage or delay to a shipment, the weight to be used in determining Carrier's limit of liability shall be the weight which is used to determine the charge for carriage of such shipment; and</div>
                <div style={{ marginLeft: '4mm', marginBottom: '1mm' }}><strong>7.2.2</strong> in the case of loss of, damage or delay to a part of a shipment, the shipment weight in 7.2.1 shall be prorated to the packages covered by the same air waybill whose value is affected by the loss, damage or delay.</div>
                <div>The weight applicable in the case of loss or damage to one or more articles in a package shall be the weight of the entire package.</div>
              </div>
            </li>

            <li style={{ marginBottom: '1mm' }} value="8">
              <div>Any exclusion or limitation of liability applicable to Carrier shall apply to Carrier's agents, employees, and representatives and to any person whose aircraft or equipment is used by Carrier for carriage and such person's agents, employees and representatives.</div>
            </li>

            <li style={{ marginBottom: '1mm' }}>
              <div style={{ marginBottom: '1mm' }}>Carrier undertakes to complete the carriage with reasonable dispatch.</div>
              <div style={{ marginBottom: '1mm' }}>Where permitted by applicable laws, tariffs and government regulations, Carrier may use alternative carriers, aircraft or modes of transport without notice but with due regard to the interests of the shipper.</div>
              <div>Carrier is authorized by the shipper to select the routing and all intermediate stopping places that it deems appropriate or to change or deviate from the routing shown on the face hereof.</div>
            </li>

            <li style={{ marginBottom: '1mm', listStyleType: 'none' }}>
              <div style={{ fontWeight: 'bold' }}>10.</div>
              <div style={{ marginLeft: '4mm' }}>
                <div style={{ marginBottom: '1mm' }}>Receipt by the person entitled to delivery of the cargo without complaint shall be prima facie evidence that the cargo has been delivered in good condition and in accordance with the contract of carriage.</div>
                <div style={{ marginBottom: '1mm' }}><strong>10.1</strong> In the case of loss of, damage or delay to cargo a written complaint must be made to Carrier by the person entitled to delivery. Such complaint must be made:</div>
                <div style={{ marginLeft: '4mm', marginBottom: '1mm' }}><strong>10.1.1</strong> in the case of damage to the cargo, immediately after discovery of the damage and at the latest within 14 days from the date of receipt of the cargo;</div>
                <div style={{ marginLeft: '4mm', marginBottom: '1mm' }}><strong>10.1.2</strong> in the case of delay, within 21 days from the date on which the cargo was placed at the disposal of the person entitled to delivery;</div>
                <div style={{ marginLeft: '4mm', marginBottom: '1mm' }}><strong>10.1.3</strong> in the case of non-delivery of the cargo, within 120 days from the date of issue of the air waybill, or if an air waybill has not been issued, within 120 days from the date of receipt of the cargo for transportation by the Carrier.</div>
                <div style={{ marginBottom: '1mm' }}><strong>10.2</strong> Such complaint may be made to the Carrier whose air waybill was used, or to the first Carrier or to the last Carrier or to the Carrier which performed the carriage during which the loss, damage or delay took place.</div>
                <div style={{ marginBottom: '1mm' }}><strong>10.3</strong> Unless a written complaint is made within the time limits specified in 10.1 no action may be brought against Carrier.</div>
                <div><strong>10.4</strong> Any rights to damages against Carrier shall be extinguished unless an action is brought within two years from the date of arrival at the destination, or from the date on which the aircraft ought to have arrived, or from the date on which the carriage stopped.</div>
              </div>
            </li>

            <li style={{ marginBottom: '1mm' }} value="11">
              <div style={{ marginBottom: '1mm' }}>Shipper shall comply with all applicable laws and government regulations of any country to or from which the cargo may be carried, including those relating to the packing, carriage or delivery of the cargo, and shall furnish such information and attach such documents to the air waybill as may be necessary to comply with such laws and regulations.</div>
              <div>Carrier is not liable to shipper and shipper shall indemnify Carrier for loss or expense due to shipper's failure to comply with this provision.</div>
            </li>

            <li style={{ marginBottom: '1mm' }}>
              <div>No agent, employee or representative of Carrier has authority to alter, modify or waive any provisions of this contract.</div>
            </li>
          </ol>
        </div>
      </div>
      </React.Fragment>
    ))}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;600;700&display=swap');
        
        @media print {
          .no-print { display: none !important; }
          @page { size: A4 portrait; margin: 0mm; }
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
          }
          .awb-print-canvas {
            position: relative !important;
            width:  210mm !important;
            height: 297mm !important;
            margin: 0 !important;
            padding: 0 !important;
            box-shadow: none !important;
            overflow: hidden !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .awb-print-canvas img {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
        @media screen {
          body {
            background: #d1d5db;
          }
          .awb-print-canvas {
            margin-top: 52px !important;
            box-shadow: 0 8px 32px rgba(0,0,0,0.25);
          }
        }
      `}</style>
    </>
  );
};

export default AirWaybillSVGPrint;
